import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";

/*
 * Слои hero (можно заменить файлы, сохранив имена):
 *   banner-sky.webp      — небо (1920x712, верхние 56.5% секции)
 *   banner-mountain.webp — горы (1920x420, прозрачный фон, низ уходит в туман)
 *   banner-winter.webp   — зима (1920x1258, полнокадровый слой секции:
 *                         верхние ~44% прозрачные, ниже — лёд и снег)
 *
 * Анимации (классы hero-* в index.css):
 *   entrance — постановочное появление слоёв с задержками
 *   idle     — парение монет/биткоина, дрейф облаков, снегопад
 *   parallax — курсор пишет --px/--py на секцию, слои двигаются
 *              с разной амплитудой (--par) по глубине
 */
import bannerSky from "../../img/home/banner-sky.webp";
import bannerMountain from "../../img/home/banner-mountain.webp";
import bannerWinter from "../../img/home/banner-winter.webp";
import bannerBitcoin from "../../img/home/banner-bitcoin.webp";
import bannerEuro from "../../img/home/banner-euro.webp";
import bannerSymbol from "../../img/home/banner-symbol.webp";
import bannerCloud from "../../img/home/banner-cloud.webp";

/* Детерминированный «рандом» снежинок — стабилен между рендерами */
const FLAKES = Array.from({ length: 28 }, (_, i) => {
  const r = (n) => ((i * n) % 97) / 97;
  return {
    left: r(37) * 100,
    size: 2 + r(53) * 4,
    dur: 9 + r(29) * 9,
    delay: -r(61) * 18,
    sx: (20 + r(43) * 60) * (i % 2 ? 1 : -1),
    op: 0.35 + r(71) * 0.45,
  };
});

function Banner() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);
  const sectionRef = useRef(null);

  /* Mouse-parallax: сглаженные --px/--py в диапазоне -1..1 */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(hover: none)").matches
    )
      return;

    let raf;
    let tx = 0,
      ty = 0,
      cx = 0,
      cy = 0;
    const clamp = (v) => Math.max(-1, Math.min(1, v));

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      /* Сцена уезжает против курсора — эффект панорамирования камеры */
      tx = -clamp(((e.clientX - r.left) / r.width) * 2 - 1);
      ty = -clamp(((e.clientY - r.top) / r.height) * 2 - 1);
    };

    const tick = () => {
      cx += (tx - cx) * 0.05;
      cy += (ty - cy) * 0.05;
      el.style.setProperty("--px", cx.toFixed(4));
      el.style.setProperty("--py", cy.toFixed(4));
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-white mt-[60px] lg:mt-[70px] h-[194vw] sm:h-[min(82.8vw,1260px)]"
    >
      {/* 1 — небо (самый дальний слой, амплитуда минимальная) */}
      <div className="hero-par absolute inset-0 [--par:8] [--par-y:5] [--par-s:1.03] pointer-events-none select-none">
        <img
          src={bannerSky}
          alt=""
          aria-hidden
          className="hero-a-fade absolute left-0 top-0 w-full h-[50%] sm:h-[56.5%] object-cover"
        />
      </div>

      {/* 2 — горы: за монетами, низ тонет в дымке зимнего слоя */}
      <div className="hero-par absolute z-10 inset-0 [--par:14] [--par-y:8] [--par-s:1.03] pointer-events-none select-none">
        <img
          src={bannerMountain}
          alt=""
          aria-hidden
          className="hero-a-up absolute left-[-21%] sm:left-0 top-[42%] sm:top-[22.3%] w-[142%] sm:w-full h-auto max-w-none"
          style={{ animationDelay: ".15s" }}
        />
      </div>

      {/* Монета евро — слева, за передним слоем */}
      <div className="hero-par z-20 absolute inset-0 [--par:34] [--par-y:18] pointer-events-none select-none">
        <div
          className="hero-a-coin-l absolute left-[6.2%] sm:left-[8.5%] top-[44.9%] sm:top-[23.5%] h-[10.1%] sm:h-[19%]"
          style={{ animationDelay: ".55s" }}
        >
          <img
            src={bannerEuro}
            alt=""
            aria-hidden
            className="hero-i-euro h-full w-auto -rotate-[24deg]"
          />
        </div>
      </div>

      {/* Символ BTC — справа, за передним слоем */}
      <div className="hero-par z-20 absolute inset-0 [--par:32] [--par-y:16] pointer-events-none select-none">
        <div
          className="hero-a-coin-r absolute left-[76.9%] sm:left-[68.5%] top-[46.5%] sm:top-[25.5%] h-[6.4%] sm:h-[12.5%]"
          style={{ animationDelay: ".65s" }}
        >
          <img
            src={bannerSymbol}
            alt=""
            aria-hidden
            className="hero-i-sym h-full w-auto rotate-[16deg]"
          />
        </div>
      </div>

      {/* 3 — зима: полнокадровый слой поверх гор и монет, низ растворяется в белый */}
      <div className="hero-par absolute inset-0 [--par:22] [--par-y:9] [--par-s:1.04] pointer-events-none select-none">
        <img
          src={bannerWinter}
          alt=""
          aria-hidden
          className="hero-a-up absolute inset-0 w-full h-full object-cover"
          style={{ animationDelay: ".3s" }}
        />
        <div
          className="hero-a-fade absolute inset-x-0 bottom-0 h-[41%] bg-[linear-gradient(to_bottom,rgba(255,255,255,0),#ffffff_80%)]"
          style={{ animationDelay: ".3s" }}
        />
      </div>

      {/* Биткоин — по центру, над слоями */}
      <div className="hero-par z-20 absolute inset-0 [--par:28] [--par-y:14] pointer-events-none select-none">
        <div
          className="hero-a-btc absolute left-[59%] sm:left-1/2 -translate-x-1/2 top-[43.6%] sm:top-[31.5%] h-[38%] sm:h-[29%] w-[67.7%] sm:w-auto overflow-hidden sm:overflow-visible"
          style={{ animationDelay: ".45s" }}
        >
          <img
            src={bannerBitcoin}
            alt=""
            aria-hidden
            className="hero-i-float absolute left-[-47%] top-[-7%] w-[185.3%] max-w-none h-auto sm:static sm:left-auto sm:top-auto sm:w-auto sm:max-w-full sm:h-full"
          />
        </div>
      </div>

      {/* Облака внизу (самый ближний слой) */}
      <div className="hero-par absolute inset-0 [--par:48] [--par-y:22] pointer-events-none select-none">
        <div
          className="hero-a-fade hidden sm:block absolute left-[50%] top-[52.5%] w-[67%]"
          style={{ animationDelay: ".9s" }}
        >
          <img src={bannerCloud} alt="" aria-hidden className="hero-cloud-a w-full h-auto" />
        </div>
        <div
          className="hero-a-fade hidden sm:block absolute left-[-29%] top-[57%] w-[75%] -scale-x-100"
          style={{ animationDelay: "1.05s" }}
        >
          <img src={bannerCloud} alt="" aria-hidden className="hero-cloud-b w-full h-auto" />
        </div>
        <div
          className="hero-a-fade hidden sm:block absolute left-[64%] top-[61%] w-[75%]"
          style={{ animationDelay: "1.2s" }}
        >
          <img src={bannerCloud} alt="" aria-hidden className="hero-cloud-c w-full h-auto" />
        </div>
      </div>

      {/* Снегопад */}
      <div
        aria-hidden
        className="hero-par absolute inset-0 [--par:40] [--par-y:10] pointer-events-none select-none"
      >
        {FLAKES.map((f, i) => (
          <span
            key={i}
            className="hero-flake absolute top-[-10px] rounded-full bg-white"
            style={{
              left: `${f.left}%`,
              width: `${f.size}px`,
              height: `${f.size}px`,
              animationDuration: `${f.dur}s`,
              animationDelay: `${f.delay}s`,
              "--sx": `${f.sx}px`,
              "--fo": f.op,
            }}
          />
        ))}
      </div>

      {/* Растворение сцены в белый: поверх всех слоёв (облака, снег, монеты),
          статичный — не двигается параллаксом, чтобы у нижней границы секции
          не оставалось жёсткого края */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 z-20 h-[38%] sm:h-[50%] pointer-events-none select-none bg-[linear-gradient(to_bottom,rgba(255,255,255,0)_0%,rgba(255,255,255,0.45)_35%,rgba(255,255,255,0.85)_65%,#ffffff_100%)]"
      />

      {/* Текст */}
      <div className="hero-par [--par:5] [--par-y:3] relative z-10 flex flex-col items-center pt-[35px] sm:pt-[42px] xl:pt-[54px] px-[18px]">
        <h1 className="hero-a-up font-['Inter'] font-black text-[#1c385f] text-center leading-[1.3] text-[40px] sm:text-[56px] xl:text-[72px]">
          {t("banner.title1")}
        </h1>

        <p
          className="hero-a-up font-['Inter'] font-black text-[#1c385f] text-center leading-[1.3] text-[23px] sm:text-[32px] xl:text-[42px]"
          style={{ animationDelay: ".12s" }}
        >
          {t("banner.title2")}
        </p>

        <div
          className="hero-a-up mt-[15px] sm:mt-[16px] px-[18px] sm:px-[28px] py-[10px] rounded-[60px] bg-[rgba(14,28,54,0.4)] backdrop-blur-[10px]"
          style={{ animationDelay: ".24s" }}
        >
          <span className="block text-center font-['Inter'] font-normal text-[14px] sm:text-[16px] text-white uppercase tracking-[0.06em] leading-[1.4]">
            {t("banner.subtitle")}
          </span>
        </div>

        <a
          href={link}
          className="hero-a-up hero-cta mt-[34px] sm:mt-[39px] inline-flex items-center justify-center px-[28px] sm:px-[42px] py-[12px] rounded-[40px] border border-white font-['Inter'] font-bold text-[14px] sm:text-[18px] xl:text-[21px] text-white capitalize backdrop-blur-[15px] hover:opacity-90 transition-opacity"
          style={{
            animationDelay: ".38s",
            backgroundImage:
              "linear-gradient(-83.64deg, rgba(14,28,54,0.4) 6%, rgba(3,65,180,0.4) 66%)",
          }}
        >
          {t("banner.cta")}
        </a>
      </div>
    </section>
  );
}

export default Banner;
